/**
 * PGSS-5309 — when to show the discrepancy banner.
 *
 * A resolved payload from Sync is still a truthy object (`has_discrepancy: false`).
 * Do not use bare truthiness on discrepancy; hide only when there is no mismatch
 * and no recorded decision to display.
 */
export const discrepancyForDisplay = (disc) =>
  disc && !disc.has_discrepancy && !disc.latest_decision ? null : (disc ?? null);

const usd = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

/** PGSS-5862: invoice minus order across the differences, to the cent. Above zero, the invoice is higher. */
export const netDifference = (rows) => {
  const cents = (rows || []).reduce((sum, row) => sum + Math.round(Number(row.delta ?? 0) * 100), 0);
  return cents / 100;
};

/** PGSS-5862: which side is higher. Neutral on purpose (PGSS-5304): either side may be the right one. */
export const differenceLabel = (delta) => {
  const amount = Number(delta ?? 0);
  if (amount === 0) return 'Same total';
  return `Invoice ${usd.format(Math.abs(amount))} ${amount > 0 ? 'higher' : 'lower'}`;
};

export const differenceSentence = (delta) => {
  const amount = Number(delta ?? 0);
  if (amount === 0) return 'The invoice and the order come to the same total.';
  return `The invoice is ${usd.format(Math.abs(amount))} ${amount > 0 ? 'higher' : 'lower'} than the order.`;
};

/** PGSS-5862: the note that brings the invoice to the order, if the order is the right one. A suggestion only. */
export const noteTypeFor = (net) => (net < 0 ? 'DEBIT' : 'CREDIT');

/**
 * PGSS-5862: the one action the panel leads with. A note changes neither side, so the difference stays after it:
 * with a note already on the invoice the next step is to keep it, not to raise another.
 *
 * @returns {'update' | 'credit' | 'debit' | 'keep'}
 */
export const recommendedAction = (discrepancy, net, noteRaised = false) => {
  if (discrepancy?.sync_available) return 'update';
  if (noteRaised || discrepancy?.has_adjustment_notes || net === 0) return 'keep';
  return noteTypeFor(net) === 'CREDIT' ? 'credit' : 'debit';
};

const whyItCannotChange = (discrepancy) => (discrepancy?.pieces_taken_out
  ? "This invoice can't be updated because pieces were taken out of a container."
  : "This invoice can't be updated because it has been sent, paid or cancelled.");

/** PGSS-5862: what the panel says above its buttons. */
export const discrepancyGuidance = (discrepancy, action, readOnly = false) => {
  if (readOnly) {
    return discrepancy?.sync_available
      ? "The customer hasn't seen this invoice yet. Use Confirm Invoice to update it to match the order, or keep it as it is."
      : 'To keep it, flag it, or raise a credit/debit note, open Review discrepancy from the Staff Invoices list.';
  }
  switch (action) {
    case 'update':
      return "The customer hasn't seen this invoice yet, so it can still be updated to match the order.";
    case 'credit':
      return `${whyItCannotChange(discrepancy)} If the order is right, a credit note lowers what the customer owes.`;
    case 'debit':
      return `${whyItCannotChange(discrepancy)} If the order is right, a debit note raises what the customer owes.`;
    default:
      if (discrepancy?.has_adjustment_notes) {
        return 'A credit or debit note is on this invoice. If it covers the difference, keep the invoice as it is to clear this warning.';
      }
      return `${whyItCannotChange(discrepancy)} The differences add up to zero, so no note is needed.`;
  }
};

const SYNC_LEFTOVER_LABELS = {
  product: 'Product',
  vas: 'VAS',
  surcharge: 'Surcharge',
  shipping: 'Shipping',
};

// Why Sync leaves each part alone, in the user's terms — the next step, not the mechanism.
const SYNC_LEFTOVER_HINTS = {
  product: 'a product may be billed on another invoice for this order',
  vas: "the VAS may be billed on another invoice, or the order's VAS items don't add up to its VAS price",
  surcharge: 'the surcharge could not be matched automatically',
  shipping: 'this button does not change shipping — edit it on the invoice',
};

const money = (value) => Number(value ?? 0).toFixed(2);

/**
 * PGSS-5612 — what to tell the user after "Update invoice to match order".
 *
 * Sync always reported success, even when the invoice still disagreed with the order (invoice 2522
 * said saved with its VAS still missing). The response carries the comparison re-run after the sync,
 * so say plainly what still differs instead.
 *
 * @returns {{ message: string, variant: 'success' | 'warning' }}
 */
export const syncResultMessage = (discrepancy) => {
  const leftovers = (discrepancy?.components || []).filter((row) => row.mismatched);
  if (!discrepancy?.has_discrepancy || leftovers.length === 0) {
    return { message: 'Invoice updated to match the order', variant: 'success' };
  }
  const parts = leftovers.map((row) => {
    const label = SYNC_LEFTOVER_LABELS[row.component] || row.component;
    const order = row.order_no || row.order_id;
    return `${label} on order ${order}: invoice ${money(row.invoice_amount)}, order ${money(row.order_amount)}`;
  });
  const hints = [...new Set(leftovers.map((row) => SYNC_LEFTOVER_HINTS[row.component]).filter(Boolean))];
  const count = leftovers.length === 1 ? 'one part still differs' : `${leftovers.length} parts still differ`;
  const pronoun = leftovers.length === 1 ? 'it' : 'them';
  return {
    message: `Invoice updated, but ${count} from the order — ${parts.join('; ')}. Sync could not change ${pronoun}: ${hints.join('; ')}.`,
    variant: 'warning',
  };
};
